import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, apiFetchJson } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { toast } from 'sonner'
import { useState } from 'react'
import {
  BookOpen,
  Plus,
  Save,
  X,
  ToggleLeft,
  ToggleRight,
  History,
} from 'lucide-react'

interface AiContext {
  id: string
  scope: 'global' | 'tenant' | 'recommendation_type'
  tenant_id: string | null
  tenant_name: string | null
  recommendation_type: string | null
  category: string | null
  title: string
  content: string
  priority: number
  is_active: boolean
  visible_to_client: boolean
  valid_from: string | null
  valid_until: string | null
  version: number
  created_at: string
  updated_at: string
}

interface ContextVersion {
  version: number
  title: string
  content: string
  updated_at: string
  updated_by: string
}

const scopeLabels: Record<string, string> = {
  global: 'Global',
  tenant: 'Tenant',
  recommendation_type: 'Tipo recomendacion',
}

const categoryLabels: Record<string, string> = {
  cost_optimization: 'Optimizacion costes',
  adoption: 'Adopcion',
  security: 'Seguridad',
  compliance: 'Cumplimiento',
}

type ScopeType = 'global' | 'tenant' | 'recommendation_type'

interface ContextFormState {
  scope: ScopeType
  tenant_id: string
  recommendation_type: string
  category: string
  title: string
  content: string
  priority: number
  is_active: boolean
  visible_to_client: boolean
  valid_from: string
  valid_until: string
}

const emptyCreate: ContextFormState = {
  scope: 'global',
  tenant_id: '',
  recommendation_type: '',
  category: 'operational',
  title: '',
  content: '',
  priority: 0,
  is_active: true,
  visible_to_client: false,
  valid_from: '',
  valid_until: '',
}

export function AdminAiContextsPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [scopeFilter, setScopeFilter] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState(emptyCreate)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState(emptyCreate)
  const [versionsId, setVersionsId] = useState<string | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'ai', 'contexts'] })

  const { data: response, isLoading } = useQuery({
    queryKey: ['admin', 'ai', 'contexts', page, scopeFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), perPage: '20' })
      if (scopeFilter) params.set('scope', scopeFilter)
      return apiFetchJson<{
        ok: boolean
        data: AiContext[]
        meta: { page: number; totalPages: number; total: number }
      }>(`/admin/ai/contexts?${params}`)
    },
  })

  const { data: versions } = useQuery({
    queryKey: ['admin', 'ai', 'contexts', versionsId, 'versions'],
    queryFn: () => api.get<ContextVersion[]>(`/admin/ai/contexts/${versionsId}/versions`),
    enabled: !!versionsId,
  })

  const createMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/ai/contexts', {
        scope: createForm.scope,
        tenantId: createForm.scope === 'tenant' && createForm.tenant_id ? createForm.tenant_id : null,
        recommendationType: createForm.scope === 'recommendation_type' && createForm.recommendation_type ? createForm.recommendation_type : null,
        category: createForm.category || 'operational',
        title: createForm.title,
        content: createForm.content,
        priority: createForm.priority,
        isActive: createForm.is_active,
        visibleToClient: createForm.visible_to_client,
        validFrom: createForm.valid_from || null,
        validUntil: createForm.valid_until || null,
      }),
    onSuccess: () => {
      toast.success('Contexto creado correctamente')
      invalidate()
      setShowCreate(false)
      setCreateForm(emptyCreate)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.put(`/admin/ai/contexts/${id}`, data),
    onSuccess: () => {
      toast.success('Contexto actualizado')
      invalidate()
      setEditingId(null)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      api.put(`/admin/ai/contexts/${id}`, { isActive: is_active }),
    onSuccess: () => {
      toast.success('Estado actualizado')
      invalidate()
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const startEdit = (ctx: AiContext) => {
    setEditingId(ctx.id)
    setEditForm({
      scope: ctx.scope,
      tenant_id: ctx.tenant_id ?? '',
      recommendation_type: ctx.recommendation_type ?? '',
      category: ctx.category || 'operational',
      title: ctx.title,
      content: ctx.content,
      priority: Number(ctx.priority) || 0,
      is_active: Boolean(ctx.is_active),
      visible_to_client: Boolean(ctx.visible_to_client),
      valid_from: ctx.valid_from ?? '',
      valid_until: ctx.valid_until ?? '',
    })
  }

  const saveEdit = (ctx: AiContext) => {
    updateMutation.mutate({
      id: ctx.id,
      data: {
        title: editForm.title,
        content: editForm.content,
        category: editForm.category || 'operational',
        priority: Number(editForm.priority),
        isActive: Boolean(editForm.is_active),
        visibleToClient: Boolean(editForm.visible_to_client),
        validFrom: editForm.valid_from || null,
        validUntil: editForm.valid_until || null,
      },
    })
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/ai/contexts/${id}`),
    onSuccess: () => { toast.success('Contexto eliminado'); invalidate() },
    onError: (err) => toast.error((err as Error).message),
  })

  const contexts = response?.data ?? []

  const renderForm = (
    form: ContextFormState,
    setForm: React.Dispatch<React.SetStateAction<ContextFormState>>,
  ) => (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      <div>
        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          Ambito *
        </label>
        <select
          value={form.scope}
          onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value as typeof form.scope }))}
          className="w-full rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <option value="global">Global</option>
          <option value="tenant">Tenant</option>
          <option value="recommendation_type">Tipo recomendacion</option>
        </select>
      </div>

      {form.scope === 'tenant' && (
        <div>
          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
            Tenant ID *
          </label>
          <input
            type="text"
            value={form.tenant_id}
            onChange={(e) => setForm((f) => ({ ...f, tenant_id: e.target.value }))}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
            placeholder="ID del tenant"
          />
        </div>
      )}

      {form.scope === 'recommendation_type' && (
        <div>
          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
            Tipo de recomendacion *
          </label>
          <input
            type="text"
            value={form.recommendation_type}
            onChange={(e) => setForm((f) => ({ ...f, recommendation_type: e.target.value }))}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
            placeholder="Tipo de recomendacion"
          />
        </div>
      )}

      <div>
        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          Tipo de contexto
        </label>
        <select
          value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          className="w-full rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <option value="operational">Operativo</option>
          <option value="commercial">Comercial</option>
          <option value="output_restriction">Restriccion de salida</option>
        </select>
      </div>

      <div className="md:col-span-2 lg:col-span-3">
        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          Titulo *
        </label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          className="w-full rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border)' }}
          placeholder="Titulo del contexto"
        />
      </div>

      <div className="md:col-span-2 lg:col-span-3">
        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          Contenido *
        </label>
        <textarea
          value={form.content}
          onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
          rows={5}
          className="w-full rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border)' }}
          placeholder="Contenido del contexto para el prompt IA..."
        />
        <p className="mt-1 text-xs" style={{ color: '#b45309' }}>
          ⚠️ RGPD: este texto se envía al proveedor LLM como parte del prompt. NO incluyas nombres de personas,
          emails, DNI, teléfonos ni datos identificativos. Describe roles, departamentos genéricos o reglas
          de negocio sin referenciar individuos.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          Prioridad
        </label>
        <input
          type="number"
          value={form.priority}
          onChange={(e) => setForm((f) => ({ ...f, priority: parseInt(e.target.value) || 0 }))}
          className="w-full rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border)' }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
            className="rounded"
          />
          Activo
        </label>
        <label className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          <input
            type="checkbox"
            checked={form.visible_to_client}
            onChange={(e) => setForm((f) => ({ ...f, visible_to_client: e.target.checked }))}
            className="rounded"
          />
          Visible para cliente
        </label>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          Valido desde
        </label>
        <input
          type="date"
          value={form.valid_from}
          onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))}
          className="w-full rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border)' }}
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          Valido hasta
        </label>
        <input
          type="date"
          value={form.valid_until}
          onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
          className="w-full rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border)' }}
        />
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="IA - Gestion de Contextos"
        description="Contextos que alimentan el motor de generacion de recomendaciones IA"
        actions={
          <Button onClick={() => setShowCreate(!showCreate)}>
            <Plus className="h-4 w-4" /> Nuevo contexto
          </Button>
        }
      />

      {/* Create form */}
      {showCreate && (
        <Card>
          <h3 className="mb-4 text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Nuevo contexto
          </h3>
          {renderForm(createForm, setCreateForm)}
          <div className="mt-4 flex gap-2">
            <Button onClick={() => createMutation.mutate()} loading={createMutation.isPending}>
              Crear contexto
            </Button>
            <Button variant="secondary" onClick={() => { setShowCreate(false); setCreateForm(emptyCreate) }}>
              Cancelar
            </Button>
          </div>
        </Card>
      )}

      {/* Scope filter */}
      <div className="flex items-center gap-3">
        <select
          value={scopeFilter}
          onChange={(e) => { setScopeFilter(e.target.value); setPage(1) }}
          className="rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <option value="">Ambito: Todos</option>
          <option value="global">Global</option>
          <option value="tenant">Tenant</option>
          <option value="recommendation_type">Tipo recomendacion</option>
        </select>
      </div>

      {/* Contexts list */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-lg border"
              style={{ backgroundColor: 'var(--color-gray-50)', borderColor: 'var(--color-border)' }}
            />
          ))}
        </div>
      ) : contexts.length === 0 ? (
        <Card>
          <EmptyState
            icon={BookOpen}
            title="Sin contextos"
            description="Crea tu primer contexto IA con el boton de arriba"
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {contexts.map((ctx) => {
            const isEditing = editingId === ctx.id
            const showingVersions = versionsId === ctx.id

            return (
              <Card key={ctx.id}>
                {/* Context header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        {ctx.title}
                      </h4>
                      <Badge variant={ctx.scope === 'global' ? 'premium' : ctx.scope === 'tenant' ? 'info' : 'neutral'}>
                        {scopeLabels[ctx.scope]}
                      </Badge>
                      {ctx.category && (
                        <Badge variant="neutral">{categoryLabels[ctx.category] ?? ctx.category}</Badge>
                      )}
                      <Badge variant={ctx.is_active ? 'success' : 'error'}>
                        {ctx.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                      <span>Prioridad: {ctx.priority}</span>
                      <span>Version: {ctx.version}</span>
                      {ctx.tenant_name && <span>Tenant: {ctx.tenant_name}</span>}
                      {ctx.recommendation_type && <span>Tipo: {ctx.recommendation_type}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleMutation.mutate({ id: ctx.id, is_active: !ctx.is_active })}
                      disabled={toggleMutation.isPending}
                    >
                      {ctx.is_active ? (
                        <ToggleRight className="h-6 w-6" style={{ color: 'var(--color-success)' }} />
                      ) : (
                        <ToggleLeft className="h-6 w-6" style={{ color: 'var(--color-text-tertiary)' }} />
                      )}
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setVersionsId(showingVersions ? null : ctx.id)}
                    >
                      <History className="h-3.5 w-3.5" /> Versiones
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => startEdit(ctx)}>
                      Editar
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Desactivar contexto "${ctx.title}"?`)) {
                          deleteMutation.mutate(ctx.id)
                        }
                      }}
                      loading={deleteMutation.isPending}
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>

                {/* Content preview */}
                {!isEditing && (
                  <p
                    className="mt-3 line-clamp-2 text-sm"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {ctx.content}
                  </p>
                )}

                {/* Inline edit form */}
                {isEditing && (
                  <div className="mt-4 border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
                    {renderForm(editForm, setEditForm)}
                    <div className="mt-4 flex gap-2">
                      <Button
                        onClick={() => saveEdit(ctx)}
                        loading={updateMutation.isPending}
                      >
                        <Save className="h-4 w-4" /> Guardar
                      </Button>
                      <Button variant="secondary" onClick={() => setEditingId(null)}>
                        <X className="h-4 w-4" /> Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Version history */}
                {showingVersions && (
                  <div className="mt-4 border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
                    <h5 className="mb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                      Historial de versiones
                    </h5>
                    {versions && versions.length > 0 ? (
                      <div className="space-y-2">
                        {versions.map((v) => (
                          <div
                            key={v.version}
                            className="rounded-md border p-3"
                            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-gray-50)' }}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                                v{v.version} — {v.title}
                              </span>
                              <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                                {formatDateTime(v.updated_at)}
                                {' por '}{v.updated_by}
                              </span>
                            </div>
                            <p className="mt-1 line-clamp-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                              {v.content}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                        Sin versiones anteriores
                      </p>
                    )}
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {(response?.meta?.totalPages ?? 1) > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
            {response?.meta?.total ?? 0} contextos
          </p>
          <div className="flex gap-1">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Anterior
            </Button>
            <span className="flex items-center px-3 text-sm tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
              {page} / {response?.meta?.totalPages ?? 1}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= (response?.meta?.totalPages ?? 1)}
              onClick={() => setPage(page + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
